import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT, TILE_SIZE, BASE_HP, BASE_UPGRADE_HP, BaseUpgradeAxis, BASE_UPGRADE_DEFS } from '@/config/GameConfig';
import { EventBus, GameEvents } from '@/events/EventBus';
import { RobotManager } from '@/systems/RobotManager';
import { GameScene, GamePhase } from '@/scenes/GameScene';
import { Robot } from '@/entities/Robot';
import { SoundManager } from '@/systems/SoundManager';
import { TECH, createTechButton } from '@/ui/TechUI';
import { GOD } from '@/config/GodMode';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { ResourceManager } from '@/systems/ResourceManager';
import { t } from '@/i18n/I18n';

/** Callback interface for actions that need UIScene coordination */
export interface RobotInspectorCallbacks {
  getPhase: () => GamePhase;
  getResources: () => number;
  getGameScene: () => GameScene | undefined;
  getRobotManager: () => RobotManager | undefined;
  getSelectedRobot: () => Robot | null;
  setSelectedRobot: (r: Robot | null) => void;
  getLastPlacedRobotId: () => string | null;
  setLastPlacedRobotId: (id: string | null) => void;
  isPlacing: () => boolean;
  refreshRoster: () => void;
  refreshSavedDesigns: () => void;
  refreshSensorForSelection: (robot: Robot | null) => void;
  openWorkshopForEdit: (robot: Robot) => void;
  displayCost: (cost: number) => number;
  buildBaseHpSegments: (x: number, y: number, w: number, h: number) => void;
  animateBaseHpBarUpgrade: (oldHp: number, oldMax: number, newHp: number, newMax: number) => void;
  getBaseHpBarBg: () => Phaser.GameObjects.Rectangle;
  setBaseHp: (hp: number) => void;
  setBaseMaxHp: (maxHp: number) => void;
}

export class RobotInspector {
  // Icons
  sellIcon!: Phaser.GameObjects.Container;
  cancelIcon!: Phaser.GameObjects.Container;
  repairIcon!: Phaser.GameObjects.Container;
  rebuildIcon!: Phaser.GameObjects.Container;
  modifyIcon!: Phaser.GameObjects.Container;
  repairAllBtn: Phaser.GameObjects.Container | null = null;

  // Base inspector
  baseInspectorPanel!: Phaser.GameObjects.Container;

  // Base range circles (one per alive node, drawn on GameScene, world-space)
  private baseRangeCircles: Phaser.GameObjects.Arc[] = [];
  // Guard: prevents panel close during upgrade animation
  private _baseUpgrading = false;

  private cb: RobotInspectorCallbacks;

  constructor(private scene: Phaser.Scene, callbacks: RobotInspectorCallbacks) {
    this.cb = callbacks;
  }

  setup(): void {}
  destroy(): void {}

  createSellIcon(): void {
    this.sellIcon = this.scene.add.container(0, 0).setDepth(200).setVisible(false);

    const w = 74, h = 24, r = 6;
    const bg = this.scene.add.graphics().setName('sellBg');
    bg.fillStyle(0xcc2222, 0.92);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
    bg.lineStyle(1, 0xff6666, 1);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    this.sellIcon.add(bg);

    // "SELL" label on the left, refund price on the right — all inside the button
    const sellLabel = this.scene.add.text(-w / 2 + 8, -1, t('workshop.sell'), {
      fontSize: '11px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    this.sellIcon.add(sellLabel);

    const refundTxt = this.scene.add.text(w / 2 - 8, -1, '', {
      fontSize: '11px', color: '#ffcc66', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(1, 0.5).setName('refundTxt');
    this.sellIcon.add(refundTxt);

    const hitZone = this.scene.add.zone(0, 0, w + 8, h + 8).setInteractive({ useHandCursor: true });
    hitZone.on('pointerdown', () => {
      const robot = this.cb.getSelectedRobot();
      if (robot && robot.alive) {
        SoundManager.play('clickCancel');
        this.deleteRobot(robot);
      }
    });
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0xff3333, 0.95);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
      bg.lineStyle(1, 0xff8888, 1);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0xcc2222, 0.92);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
      bg.lineStyle(1, 0xff6666, 1);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    });
    this.sellIcon.add(hitZone);
  }

  createCancelIcon(): void {
    this.cancelIcon = this.scene.add.container(0, 0).setDepth(200).setVisible(false);

    const w = 84, h = 24, r = 6;
    const bg = this.scene.add.graphics().setName('cancelBg');
    bg.fillStyle(0xcc8822, 0.92);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
    bg.lineStyle(1, 0xffaa44, 1);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    this.cancelIcon.add(bg);

    const txt = this.scene.add.text(-w / 2 + 8, -1, t('workshop.cancel'), {
      fontSize: '10px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    this.cancelIcon.add(txt);

    const refundTxt = this.scene.add.text(w / 2 - 8, -1, '', {
      fontSize: '10px', color: '#44ff88', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(1, 0.5).setName('cancelRefundTxt');
    this.cancelIcon.add(refundTxt);

    const hitZone = this.scene.add.zone(0, 0, w + 8, h + 8).setInteractive({ useHandCursor: true });
    hitZone.on('pointerdown', () => {
      const robot = this.cb.getSelectedRobot();
      if (robot && robot.alive) {
        SoundManager.play('clickCancel');
        this.cancelRobot(robot);
      }
    });
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0xdd9933, 0.95);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
      bg.lineStyle(1, 0xffcc66, 1);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0xcc8822, 0.92);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
      bg.lineStyle(1, 0xffaa44, 1);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    });
    this.cancelIcon.add(hitZone);
  }

  createRepairIcon(): void {
    this.repairIcon = this.scene.add.container(0, 0).setDepth(200).setVisible(false);

    const w = 84, h = 24, r = 6;
    const bg = this.scene.add.graphics().setName('repairBg');
    bg.fillStyle(0x22aa44, 0.92);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
    bg.lineStyle(1, 0x66ff88, 1);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    this.repairIcon.add(bg);

    const txt = this.scene.add.text(-w / 2 + 8, -1, t('workshop.repair'), {
      fontSize: '10px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    this.repairIcon.add(txt);

    const costTxt = this.scene.add.text(w / 2 - 8, -1, '', {
      fontSize: '10px', color: '#ffcc66', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(1, 0.5).setName('repairCostTxt');
    this.repairIcon.add(costTxt);

    const hitZone = this.scene.add.zone(0, 0, w + 8, h + 8).setInteractive({ useHandCursor: true });
    hitZone.on('pointerdown', () => {
      const robot = this.cb.getSelectedRobot();
      if (robot && robot.alive) {
        this.repairRobot(robot);
      }
    });
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x33cc55, 0.95);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
      bg.lineStyle(1, 0x88ffaa, 1);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x22aa44, 0.92);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
      bg.lineStyle(1, 0x66ff88, 1);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    });
    this.repairIcon.add(hitZone);
  }

  createRebuildIcon(): void {
    this.rebuildIcon = this.scene.add.container(0, 0).setDepth(200).setVisible(false);

    const w = 84, h = 24, r = 6;
    const bg = this.scene.add.graphics().setName('rebuildBg');
    bg.fillStyle(0xcc6622, 0.92);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
    bg.lineStyle(1, 0xff8844, 1);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    this.rebuildIcon.add(bg);

    const txt = this.scene.add.text(-w / 2 + 8, -1, t('workshop.rebuild'), {
      fontSize: '10px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    this.rebuildIcon.add(txt);

    const costTxt = this.scene.add.text(w / 2 - 8, -1, '', {
      fontSize: '10px', color: '#ffcc66', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(1, 0.5).setName('rebuildCostTxt');
    this.rebuildIcon.add(costTxt);

    const hitZone = this.scene.add.zone(0, 0, w + 8, h + 8).setInteractive({ useHandCursor: true });
    hitZone.on('pointerdown', () => {
      const robot = this.cb.getSelectedRobot();
      if (robot && robot.alive) {
        this.rebuildRobot(robot);
      }
    });
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0xdd7733, 0.95);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
      bg.lineStyle(1, 0xffaa66, 1);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0xcc6622, 0.92);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
      bg.lineStyle(1, 0xff8844, 1);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    });
    this.rebuildIcon.add(hitZone);
  }

  createModifyIcon(): void {
    this.modifyIcon = this.scene.add.container(0, 0).setDepth(200).setVisible(false);

    const w = 100, h = 24, r = 6;
    const bg = this.scene.add.graphics().setName('modifyBg');
    bg.fillStyle(0x2266cc, 0.92);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
    bg.lineStyle(1, 0x4488ff, 1);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    this.modifyIcon.add(bg);

    // Label left, cost right — same layout as SELL/CANCEL
    const txt = this.scene.add.text(-w / 2 + 8, -1, t('workshop.modify'), {
      fontSize: '11px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setName('modifyLabel');
    this.modifyIcon.add(txt);

    const costTxt = this.scene.add.text(w / 2 - 8, -1, '', {
      fontSize: '11px', color: '#ffcc66', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(1, 0.5).setName('modifyCostTxt');
    this.modifyIcon.add(costTxt);

    const hitZone = this.scene.add.zone(0, 0, w + 8, h + 8).setInteractive({ useHandCursor: true });
    hitZone.on('pointerdown', () => {
      const robot = this.cb.getSelectedRobot();
      if (robot && robot.alive) {
        this.cb.openWorkshopForEdit(robot);
      }
    });
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x3377dd, 0.95);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
      bg.lineStyle(1, 0x66aaff, 1);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x2266cc, 0.92);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
      bg.lineStyle(1, 0x4488ff, 1);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    });
    this.modifyIcon.add(hitZone);
  }

  /** Show sell & repair icons above the selected robot (workshop phase only) */
  showSellIcon(robot: Robot): void {
    if (this.cb.getPhase() !== GamePhase.WORKSHOP || this.cb.isPlacing()) {
      this.hideAllIcons();
      return;
    }
    this.cb.refreshSensorForSelection(robot);
    const gameScene = this.cb.getGameScene();
    const gameCam = gameScene?.cameras?.main;
    let sx = robot.x, sy = robot.y;
    if (gameCam) {
      const vx = gameCam.worldView.x;
      const vy = gameCam.worldView.y;
      const vw = gameCam.worldView.width;
      const vh = gameCam.worldView.height;
      sx = ((robot.x - vx) / vw) * CANVAS_WIDTH;
      sy = ((robot.y - vy) / vh) * CANVAS_HEIGHT;
    }

    const isJustPlaced = this.cb.getLastPlacedRobotId() === robot.id;

    // Collect visible buttons with their data
    const buttons: { container: Phaser.GameObjects.Container; setup: () => void }[] = [];

    // MODIFY — always visible
    buttons.push({ container: this.modifyIcon, setup: () => {
      const modFee = GOD.freeBuilding ? 0 : Math.max(
        GOD.modificationFeeMin,
        Math.round(robot.designCost * GOD.modificationFeePercent),
      );
      const modCostTxt = this.modifyIcon.getByName('modifyCostTxt') as Phaser.GameObjects.Text;
      if (modCostTxt) modCostTxt.setText(modFee > 0 ? `-$${modFee}` : t('workshop.free') ?? 'FREE');
    }});

    if (isJustPlaced) {
      // CANCEL
      buttons.push({ container: this.cancelIcon, setup: () => {
        const fullRefund = this.cb.displayCost(robot.designCost);
        const cancelRefundTxt = this.cancelIcon.getByName('cancelRefundTxt') as Phaser.GameObjects.Text;
        if (cancelRefundTxt) cancelRefundTxt.setText(`+$${fullRefund}`);
      }});
      this.sellIcon.setVisible(false);
    } else {
      // SELL
      buttons.push({ container: this.sellIcon, setup: () => {
        const refund = Math.floor(robot.designCost * 0.3);
        const refundTxt = this.sellIcon.getByName('refundTxt') as Phaser.GameObjects.Text;
        if (refundTxt) refundTxt.setText(`+$${refund}`);
      }});
      this.cancelIcon.setVisible(false);
    }

    // REPAIR or REBUILD (optional third button)
    if (!isJustPlaced && robot.needsRebuild()) {
      buttons.push({ container: this.rebuildIcon, setup: () => {
        const rebCost = robot.getRebuildCost();
        const rebCostTxt = this.rebuildIcon.getByName('rebuildCostTxt') as Phaser.GameObjects.Text;
        if (rebCostTxt) rebCostTxt.setText(`-$${rebCost}`);
        this.rebuildIcon.setAlpha(this.cb.getResources() >= rebCost ? 1 : 0.5);
      }});
      this.repairIcon.setVisible(false);
    } else if (!isJustPlaced && robot.needsRepair()) {
      buttons.push({ container: this.repairIcon, setup: () => {
        const cost = robot.getRepairCost();
        const costTxt = this.repairIcon.getByName('repairCostTxt') as Phaser.GameObjects.Text;
        if (costTxt) costTxt.setText(`-$${cost}`);
        this.repairIcon.setAlpha(this.cb.getResources() >= cost ? 1 : 0.5);
      }});
      this.rebuildIcon.setVisible(false);
    } else {
      this.repairIcon.setVisible(false);
      this.rebuildIcon.setVisible(false);
    }

    // Distribute buttons in a ring around the robot
    const radius = 46;
    const startAngle = -Math.PI / 2; // top center
    const spread = Math.PI; // distribute across top half (180°)
    const n = buttons.length;
    for (let i = 0; i < n; i++) {
      const angle = n === 1
        ? startAngle
        : startAngle - spread / 2 + (i / (n - 1)) * spread;
      const bx = sx + Math.cos(angle) * radius;
      const by = sy + Math.sin(angle) * radius;
      buttons[i].setup();
      buttons[i].container.setPosition(bx, by);
      buttons[i].container.setVisible(true);
    }
  }

  hideAllIcons(): void {
    this.sellIcon.setVisible(false);
    this.cancelIcon.setVisible(false);
    this.repairIcon.setVisible(false);
    this.rebuildIcon.setVisible(false);
    this.modifyIcon.setVisible(false);
  }

  deleteRobot(robot: Robot): void {
    if (this.cb.getPhase() !== GamePhase.WORKSHOP) return;
    const robotManager = this.cb.getRobotManager();
    const gameScene = this.cb.getGameScene();
    if (!robotManager || !gameScene) return;

    if (robot.designCost > 0) {
      const refund = Math.floor(robot.designCost * 0.3);
      gameScene.addResources(refund);
    }

    gameScene.clearFogAt(robot.x, robot.y);
    robotManager.removeRobot(robot);
    robot.destroy();

    if (this.cb.getSelectedRobot() === robot) {
      this.cb.setSelectedRobot(null);
      this.hideAllIcons();
    }

    this.cb.refreshRoster();
  }

  cancelRobot(robot: Robot): void {
    if (this.cb.getPhase() !== GamePhase.WORKSHOP) return;
    const robotManager = this.cb.getRobotManager();
    const gameScene = this.cb.getGameScene();
    if (!robotManager || !gameScene) return;

    if (robot.designCost > 0) {
      const refund = this.cb.displayCost(robot.designCost);
      gameScene.addResources(refund);
    }

    gameScene.clearFogAt(robot.x, robot.y);
    robotManager.removeRobot(robot);
    robot.destroy();

    if (this.cb.getSelectedRobot() === robot) {
      this.cb.setSelectedRobot(null);
      this.hideAllIcons();
    }
    if (this.cb.getLastPlacedRobotId() === robot.id) {
      this.cb.setLastPlacedRobotId(null);
    }

    this.cb.refreshRoster();
  }

  repairRobot(robot: Robot): void {
    if (this.cb.getPhase() !== GamePhase.WORKSHOP) return;
    const gameScene = this.cb.getGameScene();
    if (!gameScene) return;
    if (!robot.alive || !robot.needsRepair()) return;

    const cost = robot.getRepairCost();
    if (!gameScene.spendResources(cost)) return;

    robot.repairFull();
    SoundManager.play('click');
    EventBus.emit(GameEvents.ROBOT_REPAIRED, robot);

    this.spawnRepairPopup(robot);

    if (this.cb.getSelectedRobot() === robot) {
      this.showSellIcon(robot);
    }
    this.cb.refreshRoster();
  }

  rebuildRobot(robot: Robot): void {
    if (this.cb.getPhase() !== GamePhase.WORKSHOP) return;
    const gameScene = this.cb.getGameScene();
    if (!gameScene) return;
    if (!robot.alive || !robot.needsRebuild()) return;

    const cost = robot.getRebuildCost();
    if (!gameScene.spendResources(cost)) return;

    robot.rebuildFull();
    SoundManager.play('click');
    EventBus.emit(GameEvents.ROBOT_REPAIRED, robot);

    this.spawnRebuildPopup(robot);

    if (this.cb.getSelectedRobot() === robot) {
      this.showSellIcon(robot);
    }
    this.cb.refreshRoster();
  }

  repairAllRobots(): void {
    if (this.cb.getPhase() !== GamePhase.WORKSHOP) return;
    const robotManager = this.cb.getRobotManager();
    const gameScene = this.cb.getGameScene();
    if (!robotManager || !gameScene) return;

    const damaged = robotManager.robots.filter(r => r.alive && r.needsRepair());
    if (damaged.length === 0) return;

    const totalCost = damaged.reduce((sum, r) => sum + r.getRepairCost(), 0);
    if (!gameScene.spendResources(totalCost)) return;

    for (const robot of damaged) {
      robot.repairFull();
      EventBus.emit(GameEvents.ROBOT_REPAIRED, robot);
      this.spawnRepairPopup(robot);
    }

    SoundManager.play('click');
    this.cb.refreshRoster();
    const selected = this.cb.getSelectedRobot();
    if (selected && selected.alive) {
      this.showSellIcon(selected);
    }
  }

  private spawnRepairPopup(robot: Robot): void {
    const gameScene = this.cb.getGameScene();
    const gameCam = gameScene?.cameras?.main;
    let sx = robot.x, sy = robot.y;
    if (gameCam) {
      const vx = gameCam.worldView.x;
      const vy = gameCam.worldView.y;
      const vw = gameCam.worldView.width;
      const vh = gameCam.worldView.height;
      sx = ((robot.x - vx) / vw) * CANVAS_WIDTH;
      sy = ((robot.y - vy) / vh) * CANVAS_HEIGHT;
    }
    const popup = this.scene.add.text(sx, sy - 50, t('workshop.repaired'), {
      fontSize: '12px', color: '#44ff66', fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(300);
    this.scene.tweens.add({
      targets: popup,
      y: sy - 80,
      alpha: 0,
      duration: 1000,
      ease: 'Power2',
      onComplete: () => popup.destroy(),
    });
  }

  private spawnRebuildPopup(robot: Robot): void {
    const gameScene = this.cb.getGameScene();
    const gameCam = gameScene?.cameras?.main;
    let sx = robot.x, sy = robot.y;
    if (gameCam) {
      sx = ((robot.x - gameCam.worldView.x) / gameCam.worldView.width) * CANVAS_WIDTH;
      sy = ((robot.y - gameCam.worldView.y) / gameCam.worldView.height) * CANVAS_HEIGHT;
    }
    const popup = this.scene.add.text(sx, sy - 20, t('workshop.rebuild'), {
      fontSize: '12px', color: '#ffaa44', fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(300);
    this.scene.tweens.add({
      targets: popup, y: popup.y - 30, alpha: 0,
      duration: 900, ease: 'Cubic.easeOut',
      onComplete: () => popup.destroy(),
    });
  }

  trySelectRobot(px: number, py: number): void {
    const robotManager = this.cb.getRobotManager();
    if (!robotManager) return;
    for (const robot of robotManager.robots) {
      if (!robot.alive) continue;
      if (robot.distanceTo(px, py) < 20) {
        this.cb.setSelectedRobot(robot);
        this.showSellIcon(robot);
        EventBus.emit(GameEvents.ROBOT_SELECTED, robot);
        this.hideBaseInspector();
        return;
      }
    }
    // Check player base — test against each alive node, not just the centroid
    const pb = this.scene.registry.get('playerBase') as import('@/entities/GameBase').PlayerBase | undefined;
    if (pb && pb.alive && pb.isNearAnyNode(px, py, TILE_SIZE)) {
      this.cb.setSelectedRobot(null);
      this.hideAllIcons();
      EventBus.emit(GameEvents.ROBOT_SELECTED, null);
      // Always show base upgrade panel
      this.showBaseInspector();
      // During combat: also open quick-deploy panel on the left
      const phase = this.cb.getPhase();
      if (phase === GamePhase.WAVE || phase === GamePhase.INTERMISSION) {
        EventBus.emit(GameEvents.BASE_CLICKED);
      }
      return;
    }
    // Don't close the base inspector during an upgrade animation
    if (this._baseUpgrading) return;

    // Empty space
    this.cb.setSelectedRobot(null);
    this.hideAllIcons();
    this.cb.refreshSensorForSelection(null);
    this.hideBaseInspector();
    EventBus.emit(GameEvents.ROBOT_SELECTED, null);
  }

  // ── Base Inspector ──

  createBaseInspectorPanel(): void {
    this.baseInspectorPanel = this.scene.add.container(CANVAS_WIDTH, 20).setDepth(100);
    this.baseInspectorPanel.setVisible(false);
  }

  // Layout constants for base upgrade panel (shared between show & animate)
  private static readonly BUP_PANEL_W = 240;
  private static readonly BUP_AXIS_ROW_H = 46;
  private static readonly BUP_BAR_W = 80;
  private static readonly BUP_BAR_H = 8;
  private static readonly BUP_BG_Y = 28;

  showBaseInspector(): void {
    // Clear all children and rebuild
    while (this.baseInspectorPanel.list.length > 0) {
      const child = this.baseInspectorPanel.list[this.baseInspectorPanel.list.length - 1] as Phaser.GameObjects.GameObject;
      this.baseInspectorPanel.remove(child, true);
    }

    // Show turret range circle on the GameScene (world-space)
    this.showBaseRangeCircle();

    const pb = this.scene.registry.get('playerBase') as import('@/entities/GameBase').PlayerBase | undefined;
    if (!pb || !pb.alive) return;

    const { BUP_PANEL_W: panelW, BUP_AXIS_ROW_H: axisRowH, BUP_BAR_W: barW, BUP_BAR_H: barH, BUP_BG_Y: bgY } = RobotInspector;
    // Extra height for per-node HP rows when multi-base (24px per node + 14px total line)
    const multiBaseExtra = pb.isMultiBase ? pb.totalNodes * 24 + 14 : 0;
    const bgH = 72 + multiBaseExtra + BASE_UPGRADE_DEFS.length * axisRowH + 12;
    const bgX = -panelW;

    // Background with subtle glow border
    const bg = this.scene.add.rectangle(bgX + panelW / 2, bgY + bgH / 2, panelW, bgH, 0x0a0a1a, 0.94)
      .setStrokeStyle(1.5, 0x44cccc, 0.7);
    this.baseInspectorPanel.add(bg);

    // Title with icon
    const title = this.scene.add.text(bgX + 10, bgY + 8, `⛊ ${t('base.title')}`, {
      fontSize: '14px', color: '#44cccc', fontFamily: 'monospace', fontStyle: 'bold',
    });
    this.baseInspectorPanel.add(title);

    // Cannons / Range summary line
    const summaryStr = `🔫×${pb.totalCannons}  🎯${pb.bonusRangeTiles.toFixed(1)}t  🛡${Math.round(pb.armorReduction * 100)}%`;
    const summary = this.scene.add.text(bgX + panelW - 10, bgY + 10, summaryStr, {
      fontSize: '9px', color: '#778899', fontFamily: 'monospace',
    }).setOrigin(1, 0);
    this.baseInspectorPanel.add(summary);

    // HP status — per-node breakdown when multiple bases
    const lx = bgX + 10;
    let cy = bgY + 30;
    const hpBarW = panelW - 20;
    const hpBarH = 8;

    if (pb.isMultiBase) {
      // Show each base node with its name and individual HP bar
      for (const info of pb.getNodeInfos()) {
        const nodeColor = info.alive ? '#44cccc' : '#666666';
        const statusIcon = info.alive ? '◆' : '✕';
        const nodeLabel = this.scene.add.text(lx, cy, `${statusIcon} ${info.label}`, {
          fontSize: '10px', color: nodeColor, fontFamily: 'monospace', fontStyle: 'bold',
        });
        this.baseInspectorPanel.add(nodeLabel);
        const nodeHpStr = info.alive ? `${Math.ceil(info.hp)}/${info.maxHp}` : 'DESTROYED';
        const nodeHpText = this.scene.add.text(bgX + panelW - 10, cy, nodeHpStr, {
          fontSize: '9px', color: info.alive ? '#aaaacc' : '#664444', fontFamily: 'monospace',
        }).setOrigin(1, 0);
        this.baseInspectorPanel.add(nodeHpText);
        cy += 14;
        // Mini HP bar per node
        const nRatio = info.maxHp > 0 ? Math.max(0, info.hp / info.maxHp) : 0;
        const nBarBg = this.scene.add.rectangle(lx + hpBarW / 2, cy + 2, hpBarW, 4, 0x222233);
        this.baseInspectorPanel.add(nBarBg);
        const nBarColor = !info.alive ? 0x444444 : nRatio > 0.5 ? 0x44ff44 : nRatio > 0.25 ? 0xffaa00 : 0xff4444;
        const nBarFill = this.scene.add.rectangle(lx, cy, hpBarW * nRatio, 4, nBarColor).setOrigin(0, 0);
        this.baseInspectorPanel.add(nBarFill);
        cy += 10;
      }
      // Total HP summary
      const totalLabel = this.scene.add.text(lx, cy, `Total HP: ${Math.ceil(pb.hp)} / ${pb.maxHp}`, {
        fontSize: '9px', color: '#778899', fontFamily: 'monospace',
      });
      this.baseInspectorPanel.add(totalLabel);
      cy += 14;
    } else {
      // Single base — original compact display
      const hpLabel = this.scene.add.text(lx, cy, `HP: ${Math.ceil(pb.hp)} / ${pb.maxHp}`, {
        fontSize: '11px', color: '#cccccc', fontFamily: 'monospace',
      });
      this.baseInspectorPanel.add(hpLabel);
      cy += 16;
      const hpRatio = pb.maxHp > 0 ? Math.max(0, pb.hp / pb.maxHp) : 0;
      const hpBarBg = this.scene.add.rectangle(lx + hpBarW / 2, cy + hpBarH / 2, hpBarW, hpBarH, 0x222233);
      this.baseInspectorPanel.add(hpBarBg);
      const hpBarColor = hpRatio > 0.5 ? 0x44ff44 : hpRatio > 0.25 ? 0xffaa00 : 0xff4444;
      const hpBarFill = this.scene.add.rectangle(lx, cy, hpBarW * hpRatio, hpBarH, hpBarColor).setOrigin(0, 0);
      this.baseInspectorPanel.add(hpBarFill);
      const hpBarBorder = this.scene.add.rectangle(lx + hpBarW / 2, cy + hpBarH / 2, hpBarW, hpBarH)
        .setStrokeStyle(1, 0x555566).setFillStyle(0x000000, 0);
      this.baseInspectorPanel.add(hpBarBorder);
      cy += hpBarH + 10;
    }

    // Separator line
    const sep = this.scene.add.rectangle(bgX + panelW / 2, cy, panelW - 20, 1, 0x334455);
    this.baseInspectorPanel.add(sep);
    cy += 6;

    // ── Upgrade rows ──
    const isCampaign = !!this.scene.registry.get('currentLevelNode');

    for (const def of BASE_UPGRADE_DEFS) {
      const level = pb.getAxisLevel(def.axis);
      const maxed = level >= def.maxLevel;

      // Axis label
      const axisLabel = this.getAxisDisplayName(def.axis);
      const labelText = this.scene.add.text(lx, cy, axisLabel, {
        fontSize: '11px', color: '#bbbbdd', fontFamily: 'monospace', fontStyle: 'bold',
      });
      this.baseInspectorPanel.add(labelText);

      // Level bar (segments)
      const segBarX = lx + 80;
      const segW = barW / def.maxLevel;
      for (let i = 0; i < def.maxLevel; i++) {
        const filled = i < level;
        const segColor = filled ? def.color : 0x222233;
        const seg = this.scene.add.rectangle(
          segBarX + i * segW + segW / 2, cy + 5, segW - 1, barH, segColor,
        );
        if (filled) seg.setStrokeStyle(0.5, 0xffffff, 0.15);
        this.baseInspectorPanel.add(seg);
      }

      // Level text
      const lvlLabel = this.scene.add.text(segBarX + barW + 6, cy + 1, `${level}/${def.maxLevel}`, {
        fontSize: '9px', color: maxed ? '#44ff44' : '#777799', fontFamily: 'monospace',
      });
      this.baseInspectorPanel.add(lvlLabel);

      cy += 16;

      if (maxed) {
        const maxText = this.scene.add.text(lx + 6, cy, '✓ MAX', {
          fontSize: '9px', color: '#44ff44', fontFamily: 'monospace', fontStyle: 'bold',
        });
        this.baseInspectorPanel.add(maxText);
      } else {
        // Cost display + upgrade button
        const resCost = GOD.freeBuilding ? 0 : pb.getAxisUpgradeCost(def.axis);
        const currCost = GOD.freeBuilding ? 0 : pb.getAxisCurrencyCost(def.axis);
        const canAffordRes = this.cb.getResources() >= resCost;
        const canAffordCurr = !isCampaign || currCost === 0 || ProgressionManager.getCurrency() >= currCost;
        const canAfford = canAffordRes && canAffordCurr;

        // Effect preview
        const effectText = `+${def.effectPerLevel}${def.effectUnit}`;
        const preview = this.scene.add.text(lx + 6, cy, effectText, {
          fontSize: '9px', color: '#88cc88', fontFamily: 'monospace',
        });
        this.baseInspectorPanel.add(preview);

        // Cost text
        let costStr = `$${resCost}`;
        if (currCost > 0 && isCampaign) costStr += ` +${currCost}¤`;
        const costLabel = this.scene.add.text(lx + 60, cy, costStr, {
          fontSize: '9px', color: canAfford ? '#ffcc00' : '#ff4444', fontFamily: 'monospace',
        });
        this.baseInspectorPanel.add(costLabel);

        // Upgrade button
        const btnW2 = 40;
        const btnH2 = 18;
        const btnX = bgX + panelW - btnW2 - 10;
        const axis = def.axis;
        const upgradeBtn = createTechButton(this.scene, btnX + btnW2 / 2, cy + btnH2 / 2, '▲ UP', {
          onClick: () => this.performBaseAxisUpgrade(pb, axis),
          width: btnW2, height: btnH2,
          fontSize: '9px',
          color: canAfford ? 0x44ffaa : 0x444455,
        });
        this.baseInspectorPanel.add(upgradeBtn);
      }

      cy += axisRowH - 16;
    }

    this.baseInspectorPanel.setVisible(true);
    SoundManager.play('clickSelect' as any);
  }

  /** Perform a base axis upgrade with animated feedback */
  private performBaseAxisUpgrade(
    pb: import('@/entities/GameBase').PlayerBase,
    axis: BaseUpgradeAxis,
  ): void {
    if (!pb.alive || !pb.canUpgradeAxis(axis)) return;

    const resCost = GOD.freeBuilding ? 0 : pb.getAxisUpgradeCost(axis);
    const currCost = GOD.freeBuilding ? 0 : pb.getAxisCurrencyCost(axis);
    const isCampaign = !!this.scene.registry.get('currentLevelNode');

    // Check affordability
    if (this.cb.getResources() < resCost) return;
    if (isCampaign && currCost > 0 && ProgressionManager.getCurrency() < currCost) return;

    // Spend resources
    const gameScene = this.cb.getGameScene();
    if (!gameScene?.spendResources(resCost)) return;
    if (isCampaign && currCost > 0) ProgressionManager.spendCurrency(currCost);

    // Remember pre-upgrade state for animation
    const def = pb.getAxisDef(axis);
    const oldLevel = pb.getAxisLevel(axis);
    const oldMaxHp = pb.maxHp;
    const oldHp = pb.hp;

    // Apply upgrade
    pb.upgradeAxis(axis);
    const newLevel = pb.getAxisLevel(axis);

    // Update UI bars if HP changed
    if (axis === BaseUpgradeAxis.HP) {
      this.cb.setBaseMaxHp(pb.maxHp);
      this.cb.setBaseHp(pb.hp);
      this.cb.animateBaseHpBarUpgrade(oldHp, oldMaxHp, pb.hp, pb.maxHp);
    }

    // Persist
    if (isCampaign) {
      ProgressionManager.setBaseAxisLevel(axis, newLevel);
    } else {
      const levels = pb.getAllLevels();
      this.scene.registry.set('qpBaseUpgradeLevels', levels);
      // Persist to challenge treasury so upgrades carry over between challenge maps
      ResourceManager.setChallengeBaseUpgradeLevels(levels);
    }

    // ── Animated feedback ──
    this._baseUpgrading = true;

    const { BUP_PANEL_W: panelW, BUP_AXIS_ROW_H: axisRowH, BUP_BAR_W: barW, BUP_BAR_H: barH, BUP_BG_Y: bgY } = RobotInspector;
    const bgX = -panelW;
    const lx = bgX + 10;
    const segBarX = lx + 80;
    const defIndex = BASE_UPGRADE_DEFS.findIndex(d => d.axis === axis);
    // Row Y: bgY + header(30+16+8+1+6) + row offset
    const headerH = 72;  // matches showBaseInspector header height
    const rowY = bgY + headerH + defIndex * axisRowH;
    const segW = barW / def.maxLevel;
    const segX = segBarX + oldLevel * segW + segW / 2;

    // 1) Sound: evolve + coin
    SoundManager.play('evolve' as any);
    this.scene.time.delayedCall(150, () => SoundManager.play('coin' as any));

    // 2) Flash the newly filled segment white → axis color with scale punch
    const flash = this.scene.add.rectangle(segX, rowY + 5, segW - 1, barH, 0xffffff)
      .setAlpha(1).setDepth(101).setScale(1);
    this.baseInspectorPanel.add(flash);
    this.scene.tweens.add({
      targets: flash,
      scaleX: 2.5, scaleY: 2.5,
      alpha: 0,
      duration: 500,
      ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });

    // 3) Fill the segment with axis color (animated width growth)
    const fillSeg = this.scene.add.rectangle(segX, rowY + 5, 0, barH, def.color)
      .setDepth(101);
    this.baseInspectorPanel.add(fillSeg);
    this.scene.tweens.add({
      targets: fillSeg,
      displayWidth: segW - 1,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => fillSeg.destroy(),
    });

    // 4) Floating "+effect" text rising up
    const effectStr = `+${def.effectPerLevel}${def.effectUnit}`;
    const floatText = this.scene.add.text(segBarX + barW / 2, rowY - 4, effectStr, {
      fontSize: '13px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(102).setAlpha(0).setScale(0.5);
    this.baseInspectorPanel.add(floatText);
    this.scene.tweens.add({
      targets: floatText,
      y: rowY - 24,
      alpha: 1,
      scaleX: 1, scaleY: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });
    this.scene.tweens.add({
      targets: floatText,
      alpha: 0,
      delay: 400,
      duration: 400,
      ease: 'Cubic.easeIn',
      onComplete: () => floatText.destroy(),
    });

    // 5) Level text flash (Lv X → Lv X+1)
    const lvlFlash = this.scene.add.text(segBarX + barW + 6, rowY + 1, `${newLevel}/${def.maxLevel}`, {
      fontSize: '11px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 2,
    }).setDepth(102);
    this.baseInspectorPanel.add(lvlFlash);
    this.scene.tweens.add({
      targets: lvlFlash,
      alpha: 0,
      scaleX: 1.3, scaleY: 1.3,
      duration: 600,
      ease: 'Cubic.easeOut',
      onComplete: () => lvlFlash.destroy(),
    });

    // 6) Panel pulse (subtle scale bounce)
    const origX = this.baseInspectorPanel.x;
    const origScaleX = this.baseInspectorPanel.scaleX;
    const origScaleY = this.baseInspectorPanel.scaleY;
    this.scene.tweens.add({
      targets: this.baseInspectorPanel,
      scaleX: 1.02, scaleY: 1.02,
      duration: 100,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.baseInspectorPanel.scaleX = origScaleX;
        this.baseInspectorPanel.scaleY = origScaleY;
      },
    });

    // 7) Camera flash on GameScene for heavy upgrades (cannons)
    if (axis === BaseUpgradeAxis.CANNONS) {
      const gameScene = this.cb.getGameScene();
      gameScene?.cameras?.main?.flash(200, 68, 204, 204, false);
      SoundManager.play('gridExpand' as any);
    }

    // 8) Delayed rebuild
    this.scene.time.delayedCall(600, () => {
      this._baseUpgrading = false;
      if (this.baseInspectorPanel.visible) {
        this.showBaseInspector();
      }
    });
  }

  /** Get display name for a base upgrade axis */
  private getAxisDisplayName(axis: BaseUpgradeAxis): string {
    switch (axis) {
      case BaseUpgradeAxis.HP: return 'HP';
      case BaseUpgradeAxis.ARMOR: return 'ARMOR';
      case BaseUpgradeAxis.RANGE: return 'RANGE';
      case BaseUpgradeAxis.CANNONS: return 'CANNONS';
      case BaseUpgradeAxis.FIREPOWER: return 'POWER';
      default: return String(axis).toUpperCase();
    }
  }

  hideBaseInspector(): void {
    this.baseInspectorPanel.setVisible(false);
    this.hideBaseRangeCircle();
  }

  // ── Base turret range circle (world-space, drawn on GameScene) ──

  private showBaseRangeCircle(): void {
    const pb = this.scene.registry.get('playerBase') as import('@/entities/GameBase').PlayerBase | undefined;
    if (!pb || !pb.alive) return;
    const gameScene = this.cb.getGameScene();
    if (!gameScene) return;

    // Remove old circles if exists
    this.hideBaseRangeCircle();

    // Draw one range circle per alive node (not centroid)
    const range = pb.turretRange;
    const positions = pb.getAlivePositions();
    for (const pos of positions) {
      const circle = gameScene.add.circle(pos.x, pos.y, range, 0x44cccc, 0.08)
        .setStrokeStyle(1.5, 0x44cccc, 0.5)
        .setDepth(19);
      circle.setAlpha(0);
      gameScene.tweens.add({
        targets: circle,
        alpha: 1,
        duration: 250,
        ease: 'Cubic.easeOut',
      });
      this.baseRangeCircles.push(circle);
    }

  }

  private hideBaseRangeCircle(): void {
    for (const circle of this.baseRangeCircles) {
      circle.destroy();
    }
    this.baseRangeCircles = [];
  }
}
